// src/pages/super-admin/Screens/HoaAdminsPage.tsx
import React, { useMemo, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  listHoaAdmins, createHoaAdmin, updateHoaAdmin, approveHoaAdmin, rejectHoaAdmin,
  suspendHoaAdmin, reactivateHoaAdmin, resendHoaAdminCredentials,
  type HoaAdminUser, type CreateHoaAdminData,
} from "@/api/hoaAdmins";
import {
  listPlans, createPlan, updatePlan, deletePlan,
  listSubscriptions, updateSubscription,
  listSubscriptionInvoicesAdmin, verifySubscriptionInvoice, rejectSubscriptionInvoice,
  type SubscriptionPlan, type HOASubscription, type BillingCycle, type SubscriptionStatus,
  type SubscriptionInvoice,
} from "@/api/subscriptions";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Search, Download, Mail, Building2, User2,
  CheckCircle2, PauseCircle, Clock, Percent, AlertCircle, XCircle,
  CreditCard, Plus, Pencil, Trash2, Package, UserPlus,
} from "lucide-react";

const PRIMARY = "#1F3A93";
// accents
const AMBER = "#D97706";    // amber-600
const EMERALD = "#059669";  // emerald-600
const ROSE = "#E11D48";     // rose-600

const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";

// Extend UI filter to support all states
type AdminStatusUI = "ALL" | "active" | "suspended" | "inactive" | "pending" | "rejected";

/** Derive display status from both approval_status and subscription_status. */
function toUIStatus(a: HoaAdminUser): AdminStatusUI {
  if (a.approval_status === "pending_approval") return "pending";
  if (a.approval_status === "rejected") return "rejected";
  if (a.subscription_status === "active") return "active";
  if (a.subscription_status === "suspended") return "suspended";
  return "inactive";
}

type EditForm = {
  serviceChargeRate: string;
};

function csvCell(value: unknown): string {
  return JSON.stringify(value ?? "");
}

function approvalStatusEs(status: HoaAdminUser["approval_status"]): string {
  if (status === "pending_approval") return "Pendiente de aprobacion";
  if (status === "approved") return "Aprobado";
  if (status === "rejected") return "Rechazado";
  return status;
}

function subscriptionStatusEs(status: HoaAdminUser["subscription_status"]): string {
  if (status === "active") return "Activa";
  if (status === "suspended") return "Suspendida";
  if (status === "expired") return "Expirada";
  return status;
}

function exportHoaAdminsCsv(rows: HoaAdminUser[]) {
  const headers = [
    "Nombre",
    "Correo",
    "Organizacion",
    "Estado de aprobacion",
    "Estado de suscripcion",
    "Tasa de servicio",
    "Maximo de unidades",
    "Unidades usadas",
    "Puede agregar unidades",
    "Fecha de registro",
  ];

  const csv = [
    headers.join(","),
    ...rows.map((a) => {
      const fullName = [a.first_name, a.last_name].filter(Boolean).join(" ").trim() || a.email;
      const joined = a.date_joined ? new Date(a.date_joined).toLocaleDateString("es-VE") : "";
      const canAddUnits = a.can_add_units ? "Si" : "No";
      return [
        fullName,
        a.email,
        a.organization_name ?? "",
        approvalStatusEs(a.approval_status),
        subscriptionStatusEs(a.subscription_status),
        `${parseFloat(a.service_charge_rate).toFixed(2)}%`,
        a.max_units_allowed,
        a.current_unit_count,
        canAddUnits,
        joined,
      ]
        .map(csvCell)
        .join(",");
    }),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "administradores-hoa.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function HoaAdminsPage() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<AdminStatusUI>("ALL");
  const [editing, setEditing] = useState<HoaAdminUser | null>(null);
  const [form, setForm] = useState<EditForm>({ serviceChargeRate: "" });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<HoaAdminUser | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateHoaAdminData>({
    email: "", first_name: "", last_name: "", organization_name: "", max_units_allowed: 50,
  });
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: admins = [], isLoading } = useQuery({
    queryKey: ["hoa-admins"],
    queryFn: listHoaAdmins,
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof updateHoaAdmin>[1] }) =>
      updateHoaAdmin(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["hoa-admins"] }),
  });

  const approveMut = useMutation({
    mutationFn: (id: string) => approveHoaAdmin(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["hoa-admins"] }),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectHoaAdmin(id, reason),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["hoa-admins"] }); setRejectTarget(null); setRejectReason(""); },
  });

  const suspendMut = useMutation({
    mutationFn: (id: string) => suspendHoaAdmin(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["hoa-admins"] }),
  });

  const reactivateMut = useMutation({
    mutationFn: (id: string) => reactivateHoaAdmin(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["hoa-admins"] }),
  });

  const createMut = useMutation({
    mutationFn: (payload: CreateHoaAdminData) => createHoaAdmin(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hoa-admins"] });
      toast.success(t("superAdmin.hoaAdmins.alerts.created"));
      setCreateOpen(false);
      setCreateForm({ email: "", first_name: "", last_name: "", organization_name: "", max_units_allowed: 50 });
      setCreateError(null);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { email?: string[] } } })?.response?.data?.email?.[0];
      setCreateError(msg ?? t("superAdmin.hoaAdmins.alerts.createFailed"));
    },
  });

  const resendMut = useMutation({
    mutationFn: (id: string) => resendHoaAdminCredentials(id),
    onSuccess: () => {
      toast.success(t("superAdmin.hoaAdmins.alerts.resendSuccess"));
    },
    onError: () => {
      toast.error(t("superAdmin.hoaAdmins.alerts.resendFailed"));
    }
  });

  // derive filtered
  const filtered = useMemo(() => {
    const text = q.toLowerCase();
    return admins
      .filter((d) =>
        [d.first_name, d.last_name, d.email, d.organization_name ?? ""].join(" ").toLowerCase().includes(text)
      )
      .filter((d) => (statusFilter === "ALL" ? true : toUIStatus(d) === statusFilter));
  }, [admins, q, statusFilter]);

  // simple counts for header stats
  const total = admins.length;
  const pending = admins.filter((d) => d.approval_status === "pending_approval").length;
  const active = admins.filter((d) => d.approval_status === "approved" && d.subscription_status === "active").length;
  const suspended = admins.filter((d) => d.approval_status === "approved" && d.subscription_status === "suspended").length;
  const expired = admins.filter((d) => d.approval_status === "approved" && d.subscription_status === "expired").length;

  function setStatus(id: string, s: HoaAdminUser["subscription_status"]) {
    updateMut.mutate({ id, payload: { subscription_status: s } });
  }

  /** Returns the action buttons for a row based on approval + subscription state. */
  function RowActions({ a, variant }: { a: HoaAdminUser; variant: "mobile" | "desktop" }) {
    const isDesktop = variant === "desktop";
    const btnCls = isDesktop ? "h-8 px-3 rounded-xl text-white text-xs" : "rounded-xl h-9 px-3 text-sm";
    const anyPending = approveMut.isPending || rejectMut.isPending || suspendMut.isPending || reactivateMut.isPending;

    if (a.approval_status === "pending_approval") {
      return (
        <>
          <Button
            onClick={() => approveMut.mutate(a.id)}
            disabled={anyPending}
            className={`${btnCls} bg-emerald-600 hover:bg-emerald-700`} title={t("superAdmin.hoaAdmins.actions.approve")}
          >
            {t("superAdmin.hoaAdmins.actions.approve")}
          </Button>
          <Button
            onClick={() => { setRejectTarget(a); setRejectReason(""); }}
            disabled={anyPending}
            className={`${btnCls} ${isDesktop ? "" : "bg-rose-50 border border-rose-200"} text-rose-700`}
            style={isDesktop ? { backgroundColor: ROSE } : undefined}
          >
            {t("superAdmin.hoaAdmins.actions.reject")}
          </Button>
        </>
      );
    }

    if (a.approval_status === "rejected") {
      return (
        <Button
          onClick={() => approveMut.mutate(a.id)}
          disabled={anyPending}
          className={`${btnCls} bg-blue-600 hover:bg-blue-700 text-white`} title={t("superAdmin.hoaAdmins.actions.reApprove")}
        >{t("superAdmin.hoaAdmins.actions.reApprove")}</Button>
      );
    }

    // Aprobard — show Set Rate + suspend/reactivate
    return (
      <>
        <Button
          onClick={() => resendMut.mutate(a.id)}
          disabled={anyPending || resendMut.isPending}
          className={`${btnCls} bg-indigo-600 hover:bg-indigo-700 text-white`}
        >
          {t("superAdmin.hoaAdmins.actions.resendPass")}
        </Button>
        <Button
          onClick={() => onOpenEdit(a)}
          className={isDesktop ? `${btnCls} text-white` : `${btnCls} bg-amber-50 border border-amber-200 text-amber-700`}
          style={isDesktop ? { backgroundColor: AMBER } : undefined}
        >{t("superAdmin.hoaAdmins.actions.setRate")}</Button>
        {a.subscription_status === "active" ? (
          <Button
            onClick={() => suspendMut.mutate(a.id)}
            disabled={anyPending}
            className={isDesktop ? `${btnCls} text-white` : `${btnCls} bg-rose-50 border border-rose-200 text-rose-700`}
            style={isDesktop ? { backgroundColor: ROSE } : undefined}
          >{t("superAdmin.hoaAdmins.actions.suspend")}</Button>
        ) : (
          <Button
            onClick={() => reactivateMut.mutate(a.id)}
            disabled={anyPending}
            className={isDesktop ? `${btnCls} text-white` : `${btnCls} bg-emerald-50 border border-emerald-200 text-emerald-700`}
            style={isDesktop ? { backgroundColor: EMERALD } : undefined}
          >{t("superAdmin.hoaAdmins.actions.activate")}</Button>
        )}
      </>
    );
  }

  function onOpenEdit(a: HoaAdminUser) {
    setEditing(a);
    setSaveError(null);
    setForm({ serviceChargeRate: parseFloat(a.service_charge_rate).toString() });
  }

  async function saveEdit() {
    if (!editing) return;
    setSaveError(null);
    const rate = parseFloat(form.serviceChargeRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      setSaveError(t("superAdmin.hoaAdmins.alerts.invalidCharge"));
      return;
    }
    try {
      await updateMut.mutateAsync({ id: editing.id, payload: { service_charge_rate: rate } });
      setEditing(null);
    } catch {
      setSaveError(t("superAdmin.hoaAdmins.alerts.saveFailed"));
    }
  }

  // close dialog resets form
  useEffect(() => {
    if (!editing) { setForm({ serviceChargeRate: "" }); setSaveError(null); }
  }, [editing]);

  // rail color per status
  const railColor = (a: HoaAdminUser) => {
    if (a.approval_status === "pending_approval") return "border-amber-400";
    if (a.approval_status === "rejected") return "border-gray-400";
    if (a.subscription_status === "active") return "border-emerald-400";
    if (a.subscription_status === "suspended") return "border-rose-400";
    return "border-gray-300";
  };

  return (
    <div className="space-y-5 sm:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Heading */}
      <Card variant="glass" className="rounded-3xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Building2 className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-[18px] sm:text-[20px] font-semibold tracking-[-0.01em] text-gray-900">
              {t("superAdmin.hoaAdmins.header.title")}
            </h1>
            <p className="text-gray-600 mt-1 text-sm">
              {t("superAdmin.hoaAdmins.header.desc")}
            </p>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="admins" className="w-full">
        <TabsList className="rounded-2xl bg-white ring-1 ring-black/10 shadow-sm p-1 mb-1 w-full sm:w-auto">
          <TabsTrigger value="admins" className="rounded-xl flex items-center gap-2">
            <User2 className="w-4 h-4" />{t("superAdmin.hoaAdmins.tabs.admins")}</TabsTrigger>
          <TabsTrigger value="subscriptions" className="rounded-xl flex items-center gap-2">
            <CreditCard className="w-4 h-4" />{t("superAdmin.hoaAdmins.tabs.subscriptions")}</TabsTrigger>
          <TabsTrigger value="sub-payments" className="rounded-xl flex items-center gap-2">
            <CreditCard className="w-4 h-4" />{t("superAdmin.hoaAdmins.tabs.subPayments")}</TabsTrigger>
        </TabsList>

        {/* ── TAB 1: HOA Admins ─────────────────────────────────── */}
        <TabsContent value="admins" className="space-y-5 sm:space-y-6 mt-4">
          {/* Stats rail */}
          <Card variant="glass" rail railColor="border-blue-400" railWidth="border-l-4" className="rounded-3xl p-5 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Stat title={t("superAdmin.hoaAdmins.stats.total")} value={total} icon={<User2 className="w-4 h-4" />} />
              <Stat title={t("superAdmin.hoaAdmins.stats.pending")} value={pending} icon={<AlertCircle className="w-4 h-4" />} tone="amber" />
              <Stat title={t("superAdmin.hoaAdmins.stats.active")} value={active} icon={<CheckCircle2 className="w-4 h-4" />} tone="emerald" />
              <Stat title={t("superAdmin.hoaAdmins.stats.suspended")} value={suspended} icon={<PauseCircle className="w-4 h-4" />} tone="rose" />
              <Stat title={t("superAdmin.hoaAdmins.stats.expired")} value={expired} icon={<Clock className="w-4 h-4" />} tone="slate" />
            </div>
          </Card>

          {/* Toolbar */}
          <Card variant="glass" className="rounded-3xl p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("superAdmin.hoaAdmins.filters.search")}
                  className="pl-9"
                />
              </div>
              <div className="sm:ml-auto flex gap-2 items-center">
                <div className="inline-flex items-center gap-2">
                  <span className="text-sm text-gray-600">{t("superAdmin.hoaAdmins.filters.status")}:</span>
                  <StatusSelect value={statusFilter} onChange={setStatusFilter} />
                </div>
                <Button
                  variant="outline"
                  onClick={() => exportHoaAdminsCsv(filtered)}
                  className={`gap-2 ${QUIET}`}
                  title={t("superAdmin.hoaAdmins.actions.exportCsv")}
                >
                  <Download className="w-4 h-4" />{t("superAdmin.hoaAdmins.actions.exportCsv")}</Button>
                <Button
                  onClick={() => { setCreateOpen(true); setCreateError(null); }}
                  className="gap-2 rounded-xl text-white"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <UserPlus className="w-4 h-4" />{t("superAdmin.hoaAdmins.actions.createAccount")}</Button>
              </div>
            </div>
          </Card>

          {/* Mobile list (≤ md) */}
          <div className="md:hidden space-y-3">
            {isLoading && <Card className="rounded-2xl p-6 text-center text-gray-500">{t("superAdmin.hoaAdmins.states.loading")}</Card>}
            {filtered.map((a) => (
              <Card
                key={a.id}
                rail
                railColor={railColor(a)}
                railWidth="border-l-4"
                className="rounded-2xl p-4 bg-white/80 backdrop-blur ring-1 ring-black/5"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 rounded-xl p-2 bg-white ring-1 ring-black/10 shadow-sm">
                    <User2 className="w-4 h-4 text-gray-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-gray-900 truncate">
                        {[a.first_name, a.last_name].filter(Boolean).join(" ") || a.email}
                      </div>
                      <StatusPill admin={a} />
                    </div>
                    <div className="mt-1 text-sm text-gray-700 break-words inline-flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500 shrink-0" />
                      <span className="break-words">{a.email}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-700 break-words inline-flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-500 shrink-0" />
                      <span className="break-words">{a.organization_name || <span className="text-gray-400">{t("superAdmin.hoaAdmins.states.noOrg")}</span>}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-700 inline-flex items-center gap-2">
                      <Percent className="w-4 h-4 text-gray-500 shrink-0" />
                      <span>{parseFloat(a.service_charge_rate).toFixed(2)}{t("superAdmin.hoaAdmins.text.serviceChargeSuffix")}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <RowActions a={a} variant="mobile" />
                </div>
              </Card>
            ))}
            {!isLoading && filtered.length === 0 && (
              <Card className="rounded-2xl p-6 text-center text-gray-500">{t("superAdmin.hoaAdmins.states.noResults")}</Card>
            )}
          </div>

          {/* Desktop table (≥ md) */}
          <Card className="rounded-2xl hidden md:block">
            <div className="p-4 md:p-5">
              <div className="overflow-x-auto w-full">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("superAdmin.hoaAdmins.table.name")}</TableHead>
                      <TableHead>{t("superAdmin.hoaAdmins.table.email")}</TableHead>
                      <TableHead>{t("superAdmin.hoaAdmins.table.org")}</TableHead>
                      <TableHead>{t("superAdmin.hoaAdmins.table.status")}</TableHead>
                      <TableHead>{t("superAdmin.hoaAdmins.table.charge")}</TableHead>
                      <TableHead className="text-right">{t("superAdmin.hoaAdmins.table.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-gray-500 py-6">{t("superAdmin.hoaAdmins.states.loading")}</TableCell>
                      </TableRow>
                    )}
                    {filtered.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="font-medium">
                          {[a.first_name, a.last_name].filter(Boolean).join(" ") || a.email}
                        </TableCell>
                        <TableCell className="break-words">{a.email}</TableCell>
                        <TableCell className="break-words">{a.organization_name || <span className="text-gray-400">{t("common.none")}</span>}</TableCell>
                        <TableCell><StatusPill admin={a} /></TableCell>
                        <TableCell className="font-mono">
                          {parseFloat(a.service_charge_rate).toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right space-x-2 whitespace-nowrap">
                          <RowActions a={a} variant="desktop" />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!isLoading && filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-gray-500 py-6">
                          {t("superAdmin.hoaAdmins.states.noResults")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ── TAB 2: Subscriptions ───────────────────────────────── */}
        <TabsContent value="subscriptions" className="space-y-6 mt-4">
          <SubscriptionsTab />
        </TabsContent>

        <TabsContent value="sub-payments" className="space-y-6 mt-4">
          <SubscriptionPaymentsTab />
        </TabsContent>
      </Tabs>

      {/* Create HOA Account dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) { setCreateOpen(false); setCreateError(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("superAdmin.hoaAdmins.dialogs.createAccount")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 -mt-1">
            {t("superAdmin.hoaAdmins.form.createNotice")}
          </p>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.email")} <span className="text-rose-500">*</span></label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                placeholder={t("superAdmin.hoaAdmins.form.email")}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.firstName")}</label>
                <Input
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, first_name: e.target.value }))}
                  placeholder={t("superAdmin.hoaAdmins.form.firstName")}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.lastName")}</label>
                <Input
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, last_name: e.target.value }))}
                  placeholder={t("superAdmin.hoaAdmins.form.lastName")}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.orgName")}</label>
              <Input
                value={createForm.organization_name ?? ""}
                onChange={(e) => setCreateForm((f) => ({ ...f, organization_name: e.target.value }))}
                placeholder={t("superAdmin.hoaAdmins.form.orgName")}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.maxUnitsAllowed")}</label>
              <Input
                type="number"
                min={1}
                value={createForm.max_units_allowed ?? 50}
                onChange={(e) => setCreateForm((f) => ({ ...f, max_units_allowed: parseInt(e.target.value) || 50 }))}
                className="mt-1"
              />
            </div>
            {createError && <p className="text-sm text-rose-600">{createError}</p>}
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={() => {
                if (!createForm.email.trim()) { setCreateError(t("validation.required")); return; }
                createMut.mutate(createForm);
              }}
              disabled={createMut.isPending}
              className="rounded-xl text-white"
              style={{ backgroundColor: PRIMARY }}
            >
              {createMut.isPending ? t("settings.saving") : t("superAdmin.hoaAdmins.actions.createAccount")}
            </Button>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl">
              {t("superAdmin.hoaAdmins.actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rechazar dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("superAdmin.hoaAdmins.dialogs.rejectAccount")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            <strong>{[rejectTarget?.first_name, rejectTarget?.last_name].filter(Boolean).join(" ") || rejectTarget?.email}</strong>.
            {t("superAdmin.hoaAdmins.dialogs.rejectNotice")}
          </p>
          <div className="space-y-2 mt-2">
            <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.reasonOptional")}</label>
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("superAdmin.hoaAdmins.form.reasonOptional")}
            />
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={() => rejectTarget && rejectMut.mutate({ id: rejectTarget.id, reason: rejectReason })}
              disabled={rejectMut.isPending}
              className="rounded-xl text-white"
              style={{ backgroundColor: ROSE }}
            >
              {rejectMut.isPending ? t("superAdmin.payments.actions.rejecting") : t("superAdmin.hoaAdmins.actions.reject")}
            </Button>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(""); }} className="rounded-xl">
              {t("superAdmin.hoaAdmins.actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(isOpen) => { if (!isOpen) setEditing(null) }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("superAdmin.hoaAdmins.headers.charges")}</DialogTitle>
          </DialogHeader>

          {editing && (
            <p className="text-sm text-gray-600 -mt-1">
              {t("superAdmin.hoaAdmins.headers.settingRateFor")} <strong>{[editing.first_name, editing.last_name].filter(Boolean).join(" ") || editing.email}</strong>
              {editing.organization_name ? ` · ${editing.organization_name}` : ""}
            </p>
          )}

          <div className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.chargeRate")}</label>
              <p className="text-xs text-gray-500 mb-1.5">
                {t("superAdmin.hoaAdmins.form.chargeNotice")}
              </p>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.serviceChargeRate}
                  onChange={(e) => setForm((f) => ({ ...f, serviceChargeRate: e.target.value }))}
                  placeholder={t("superAdmin.hoaAdmins.form.chargeRate")}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
              </div>
              {saveError && <p className="text-sm text-rose-600 mt-1">{saveError}</p>}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              onClick={saveEdit}
              disabled={updateMut.isPending}
              className="rounded-xl text-white"
              style={{ backgroundColor: AMBER }}
            >
              {updateMut.isPending ? t("settings.saving") : t("settings.save")}
            </Button>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl">
              {t("superAdmin.hoaAdmins.actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Subscriptions Tab — plans + per-admin subscription management
───────────────────────────────────────────────────────────────── */

type PlanForm = {
  name: string;
  price_usd: string;
  billing_cycle: BillingCycle;
  max_units: string;
  description: string;
  is_active: boolean;
};

const EMPTY_PLAN_FORM: PlanForm = {
  name: "",
  price_usd: "",
  billing_cycle: "monthly",
  max_units: "50",
  description: "",
  is_active: true,
};

function SubscriptionsTab() {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const dateLocale = language === "es" ? "es-ES" : "en-US";

  // ── Plans state ─────────────────────────────────
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planForm, setPlanForm] = useState<PlanForm>(EMPTY_PLAN_FORM);
  const [planFormError, setPlanFormError] = useState<string | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<SubscriptionPlan | null>(null);

  // ── Subscription assignment state ───────────────
  const [assignTarget, setAssignTarget] = useState<HOASubscription | null>(null);
  const [assignPlanId, setAssignPlanId] = useState<string>("");
  const [assignStatus, setAssignStatus] = useState<SubscriptionStatus>("active");
  const [assignNotes, setAssignNotes] = useState("");

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: listPlans,
  });

  const { data: subscriptions = [], isLoading: subsLoading } = useQuery({
    queryKey: ["hoa-subscriptions"],
    queryFn: listSubscriptions,
  });

  const createPlanMut = useMutation({
    mutationFn: (d: PlanForm) =>
      createPlan({
        name: d.name,
        price_usd: parseFloat(d.price_usd),
        billing_cycle: d.billing_cycle,
        max_units: parseInt(d.max_units),
        description: d.description,
        is_active: d.is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      setCreatingPlan(false);
      setPlanForm(EMPTY_PLAN_FORM);
    },
    onError: () => setPlanFormError(t("superAdmin.hoaAdmins.alerts.createFailed")),
  });

  const updatePlanMut = useMutation({
    mutationFn: ({ id, d }: { id: string; d: PlanForm }) =>
      updatePlan(id, {
        name: d.name,
        price_usd: parseFloat(d.price_usd),
        billing_cycle: d.billing_cycle,
        max_units: parseInt(d.max_units),
        description: d.description,
        is_active: d.is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      setEditingPlan(null);
    },
    onError: () => setPlanFormError(t("superAdmin.hoaAdmins.alerts.saveFailed")),
  });

  const deletePlanMut = useMutation({
    mutationFn: (id: string) => deletePlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      setDeletingPlan(null);
    },
    onError: (err: unknown) => {
      const apiError = err as { response?: { data?: { detail?: string; code?: string } } };
      const detail = apiError?.response?.data?.detail;
      const code = apiError?.response?.data?.code;

      if (code === "PLAN_IN_USE") {
        toast.error(t("superAdmin.hoaAdmins.alerts.deletePlanInUse"));
        return;
      }

      toast.error(detail ?? t("superAdmin.hoaAdmins.alerts.deletePlanFailed"));
    },
  });

  const updateSubMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof updateSubscription>[1] }) =>
      updateSubscription(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hoa-subscriptions"] });
      setAssignTarget(null);
    },
  });

  function openCreatePlan() {
    setPlanForm(EMPTY_PLAN_FORM);
    setPlanFormError(null);
    setCreatingPlan(true);
  }

  function openEditPlan(p: SubscriptionPlan) {
    setPlanForm({
      name: p.name,
      price_usd: p.price_usd,
      billing_cycle: p.billing_cycle,
      max_units: p.max_units.toString(),
      description: p.description,
      is_active: p.is_active,
    });
    setPlanFormError(null);
    setEditingPlan(p);
  }

  function validatePlanForm(): boolean {
    if (!planForm.name.trim()) { setPlanFormError(t("validation.required")); return false; }
    const price = parseFloat(planForm.price_usd);
    if (isNaN(price) || price < 0) { setPlanFormError(t("validation.required")); return false; }
    const units = parseInt(planForm.max_units);
    if (isNaN(units) || units < 0) { setPlanFormError(t("validation.required")); return false; }
    return true;
  }

  function submitPlanForm() {
    setPlanFormError(null);
    if (!validatePlanForm()) return;
    if (editingPlan) {
      updatePlanMut.mutate({ id: editingPlan.id, d: planForm });
    } else {
      createPlanMut.mutate(planForm);
    }
  }

  function openAssign(sub: HOASubscription) {
    setAssignTarget(sub);
    setAssignPlanId(sub.plan ?? "");
    setAssignStatus(sub.status);
    setAssignNotes(sub.notes);
  }

  const cycleLabel = (c: BillingCycle, t: any) => {
    const map: Record<BillingCycle, string> = {
    monthly: t("superAdmin.hoaAdmins.cycles.monthly"),
    quarterly: t("superAdmin.hoaAdmins.cycles.quarterly"),
    annual: t("superAdmin.hoaAdmins.cycles.annual"),
  };
    return map[c];
  };

  const subStatusLabel = (status: SubscriptionStatus) => {
    const labels: Record<SubscriptionStatus, string> = {
      trial: t("superAdmin.hoaAdmins.status.trial"),
      active: t("superAdmin.hoaAdmins.status.active"),
      suspended: t("superAdmin.hoaAdmins.status.suspended"),
      cancelled: t("superAdmin.hoaAdmins.status.cancelled"),
      expired: t("superAdmin.hoaAdmins.status.expired"),
    };
    return labels[status];
  };

  const subStatusColors: Record<SubscriptionStatus, string> = {
    trial: "bg-blue-100 text-blue-900 ring-blue-300",
    active: "bg-emerald-100 text-emerald-900 ring-emerald-300",
    suspended: "bg-rose-100 text-rose-900 ring-rose-300",
    cancelled: "bg-gray-100 text-gray-700 ring-gray-300",
    expired: "bg-amber-100 text-amber-900 ring-amber-300",
  };

  const mutIsPending = createPlanMut.isPending || updatePlanMut.isPending;

  return (
    <>
      {/* ── Plans section ──────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-gray-700" />
            <h2 className="text-base font-semibold text-gray-900">{t("superAdmin.hoaAdmins.headers.plans")}</h2>
          </div>
          <Button
            onClick={openCreatePlan}
            className="rounded-xl text-white gap-2 text-sm"
            style={{ backgroundColor: PRIMARY }}
          >
            <Plus className="w-4 h-4" /> {t("superAdmin.hoaAdmins.subs.newPlan")}
          </Button>
        </div>

        {plansLoading && (
          <Card className="rounded-2xl p-5 text-center text-gray-500">{t("superAdmin.hoaAdmins.states.loadingPlans")}</Card>
        )}
        {!plansLoading && plans.length === 0 && (
          <Card className="rounded-2xl p-6 text-center text-gray-400">
            {t("superAdmin.hoaAdmins.states.noPlansFound")}
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              rail
              railColor={plan.is_active ? "border-emerald-400" : "border-gray-300"}
              railWidth="border-l-4"
              className="rounded-2xl p-5 bg-white/90 ring-1 ring-black/5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{plan.name}</div>
                  <div className="text-2xl font-bold tabular-nums text-gray-900 mt-1">
                    ${parseFloat(plan.price_usd).toFixed(2)}
                    <span className="text-sm font-normal text-gray-500 ml-1">/ {cycleLabel(plan.billing_cycle, t).toLowerCase()}</span>
                  </div>
                </div>
                <Badge className={plan.is_active ? "bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300 shrink-0" : "bg-gray-100 text-gray-600 ring-1 ring-gray-300 shrink-0"}>
                  {plan.is_active ? t("superAdmin.hoaAdmins.status.active") : t("superAdmin.hoaAdmins.status.inactive")}
                </Badge>
              </div>

              <div className="mt-2 space-y-1 text-sm text-gray-600">
                <div className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  {plan.max_units === 0
                    ? t("superAdmin.hoaAdmins.form.zeroUnlimited")
                    : `${plan.max_units} ${t("superAdmin.hoaAdmins.form.maxUnits").toLowerCase()}`}
                </div>
                {plan.description && (
                  <p className="text-gray-500 text-xs line-clamp-2 mt-1">{plan.description}</p>
                )}
              </div>

              <div className="mt-3 text-xs text-gray-400">
                {plan.subscription_count} {t("superAdmin.hoaAdmins.table.adminHoa")}
              </div>

              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => openEditPlan(plan)}
                  className="flex-1 rounded-xl h-8 text-xs gap-1.5"
                >
                  <Pencil className="w-3 h-3" /> {t("superAdmin.hoaAdmins.actions.edit")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeletingPlan(plan)}
                  className="rounded-xl h-8 text-xs px-3 text-rose-600 border-rose-200 hover:bg-rose-50"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* ── HOA Admin Subscriptions section ───────────── */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-gray-700" />
          <h2 className="text-base font-semibold text-gray-900">{t("superAdmin.hoaAdmins.headers.subs")}</h2>
        </div>

        <Card className="rounded-2xl hidden md:block">
          <div className="p-4 md:p-5 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("superAdmin.hoaAdmins.table.adminHoa")}</TableHead>
                  <TableHead>{t("superAdmin.hoaAdmins.table.org")}</TableHead>
                  <TableHead>{t("superAdmin.hoaAdmins.table.plan")}</TableHead>
                  <TableHead>{t("superAdmin.hoaAdmins.table.status")}</TableHead>
                  <TableHead>{t("superAdmin.hoaAdmins.table.period")}</TableHead>
                  <TableHead className="text-right">{t("superAdmin.hoaAdmins.table.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subsLoading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-6">{t("superAdmin.hoaAdmins.states.loading")}</TableCell>
                  </TableRow>
                )}
                {subscriptions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <div className="font-medium">{sub.hoa_admin_name}</div>
                      <div className="text-xs text-gray-500">{sub.hoa_admin_email}</div>
                    </TableCell>
                    <TableCell className="text-gray-600">{sub.organization_name || <span className="text-gray-400">{t("common.none")}</span>}</TableCell>
                    <TableCell>
                      {sub.plan_detail ? (
                        <div>
                          <div className="font-medium text-sm">{sub.plan_detail.name}</div>
                          <div className="text-xs text-gray-500">
                            ${parseFloat(sub.plan_detail.price_usd).toFixed(2)}/{cycleLabel(sub.plan_detail.billing_cycle as BillingCycle, t).toLowerCase()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">{t("superAdmin.hoaAdmins.states.noPlan")}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`ring-1 ${subStatusColors[sub.status] ?? "bg-gray-100 text-gray-700 ring-gray-300"}`}>
                        {subStatusLabel(sub.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {sub.current_period_end
                        ? new Date(sub.current_period_end).toLocaleDateString(dateLocale)
                        : sub.trial_ends_at
                        ? `${t("superAdmin.hoaAdmins.status.trial")}: ${new Date(sub.trial_ends_at).toLocaleDateString(dateLocale)}`
                        : <span className="text-gray-400">{t("common.none")}</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        onClick={() => openAssign(sub)}
                        className="rounded-xl h-8 text-xs gap-1.5"
                      >
                        <Pencil className="w-3 h-3" /> {t("superAdmin.hoaAdmins.actions.manage")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!subsLoading && subscriptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-6">
                      {t("superAdmin.hoaAdmins.states.noSubsFound")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Mobile subscription cards */}
        <div className="md:hidden space-y-3">
          {subsLoading && <Card className="rounded-2xl p-5 text-center text-gray-500">{t("superAdmin.hoaAdmins.states.loading")}</Card>}
          {subscriptions.map((sub) => (
            <Card key={sub.id} className="rounded-2xl p-4 bg-white/90 ring-1 ring-black/5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{sub.hoa_admin_name}</div>
                  <div className="text-xs text-gray-500">{sub.hoa_admin_email}</div>
                  {sub.organization_name && (
                    <div className="text-xs text-gray-500 mt-0.5">{sub.organization_name}</div>
                  )}
                </div>
                <Badge className={`ring-1 shrink-0 ${subStatusColors[sub.status] ?? "bg-gray-100 text-gray-700 ring-gray-300"}`}>
                  {subStatusLabel(sub.status)}
                </Badge>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                {sub.plan_detail ? (
                  <span>
                    {sub.plan_detail.name} · ${parseFloat(sub.plan_detail.price_usd).toFixed(2)}/{cycleLabel(sub.plan_detail.billing_cycle as BillingCycle, t).toLowerCase()}
                  </span>
                ) : (
                  <span className="text-gray-400">{t("superAdmin.hoaAdmins.states.noPlanAssigned")}</span>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => openAssign(sub)}
                className="mt-3 w-full rounded-xl h-9 text-sm gap-2"
              >
                <Pencil className="w-3.5 h-3.5" /> {t("superAdmin.hoaAdmins.actions.manageSub")}
              </Button>
            </Card>
          ))}
          {!subsLoading && subscriptions.length === 0 && (
            <Card className="rounded-2xl p-5 text-center text-gray-400">{t("superAdmin.hoaAdmins.states.noSubscriptions")}</Card>
          )}
        </div>
      </div>

      {/* Plan create/edit dialog */}
      <Dialog
        open={creatingPlan || !!editingPlan}
        onOpenChange={(open) => { if (!open) { setCreatingPlan(false); setEditingPlan(null); } }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPlan ? t("superAdmin.hoaAdmins.dialogs.editPlan") : t("superAdmin.hoaAdmins.dialogs.newPlan")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.planName")}</label>
              <Input
                value={planForm.name}
                onChange={(e) => setPlanForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t("common.name")}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.priceUsd")}</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={planForm.price_usd}
                    onChange={(e) => setPlanForm((f) => ({ ...f, price_usd: e.target.value }))}
                    placeholder={t("superAdmin.hoaAdmins.form.priceUsd")}
                    className="pl-7"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.billingCycle")}</label>
                <select
                  value={planForm.billing_cycle}
                  onChange={(e) => setPlanForm((f) => ({ ...f, billing_cycle: e.target.value as BillingCycle }))}
                  className="mt-1 w-full rounded-xl border border-black/10 ring-1 ring-black/5 bg-white px-3 py-2 text-sm text-gray-900"
                >
                  <option value="monthly">{t("superAdmin.hoaAdmins.cycles.monthly")}</option>
                  <option value="quarterly">{t("superAdmin.hoaAdmins.cycles.quarterly")}</option>
                  <option value="annual">{t("superAdmin.hoaAdmins.cycles.annual")}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.maxUnits")}</label>
              <p className="text-xs text-gray-500">{t("superAdmin.hoaAdmins.form.zeroUnlimited")}</p>
              <Input
                type="number"
                min="0"
                value={planForm.max_units}
                onChange={(e) => setPlanForm((f) => ({ ...f, max_units: e.target.value }))}
                placeholder={t("superAdmin.hoaAdmins.form.maxUnits")}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.form.description")}</label>
              <Textarea
                value={planForm.description}
                onChange={(e) => setPlanForm((f) => ({ ...f, description: e.target.value }))}
                placeholder={t("superAdmin.hoaAdmins.form.description")}
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="plan-active"
                checked={planForm.is_active}
                onChange={(e) => setPlanForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <label htmlFor="plan-active" className="text-sm text-gray-700">{t("superAdmin.hoaAdmins.form.activeAvailable")}</label>
            </div>
            {planFormError && <p className="text-sm text-rose-600">{planFormError}</p>}
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={submitPlanForm}
              disabled={mutIsPending}
              className="rounded-xl text-white"
              style={{ backgroundColor: PRIMARY }}
            >
              {mutIsPending ? t("settings.saving") : editingPlan ? t("settings.saveChanges") : t("superAdmin.hoaAdmins.subs.newPlan")}
            </Button>
            <Button variant="outline" onClick={() => { setCreatingPlan(false); setEditingPlan(null); }} className="rounded-xl">
              {t("superAdmin.hoaAdmins.actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Eliminar plan confirm dialog */}
      <Dialog open={!!deletingPlan} onOpenChange={(open) => { if (!open) setDeletingPlan(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("superAdmin.hoaAdmins.dialogs.deletePlan")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            {t("superAdmin.hoaAdmins.dialogs.deletePlan")} <strong>{deletingPlan?.name}</strong>?
            {deletingPlan && deletingPlan.subscription_count > 0 && (
              <span className="text-amber-700"> {deletingPlan.subscription_count} {t("superAdmin.hoaAdmins.headers.subs")}</span>
            )}
          </p>
          <DialogFooter className="mt-4">
            <Button
              onClick={() => deletingPlan && deletePlanMut.mutate(deletingPlan.id)}
              disabled={deletePlanMut.isPending}
              className="rounded-xl text-white bg-rose-600 hover:bg-rose-700"
            >
              {deletePlanMut.isPending ? t("hoa.announcements.deleting") : t("superAdmin.hoaAdmins.actions.delete")}
            </Button>
            <Button variant="outline" onClick={() => setDeletingPlan(null)} className="rounded-xl">
              {t("superAdmin.hoaAdmins.actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign / manage subscription dialog */}
      <Dialog open={!!assignTarget} onOpenChange={(open) => { if (!open) setAssignTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("superAdmin.hoaAdmins.dialogs.manageSub")}</DialogTitle>
          </DialogHeader>
          {assignTarget && (
            <p className="text-sm text-gray-600 -mt-1">
              <strong>{assignTarget.hoa_admin_name}</strong>
              {assignTarget.organization_name ? ` · ${assignTarget.organization_name}` : ""}
            </p>
          )}
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.table.subscriptionPlan")}</label>
              <select
                value={assignPlanId}
                onChange={(e) => setAssignPlanId(e.target.value)}
                className="mt-1 w-full rounded-xl border border-black/10 ring-1 ring-black/5 bg-white px-3 py-2 text-sm text-gray-900"
              >
                <option value="">{t("superAdmin.hoaAdmins.states.noPlanList")}</option>
                {plans.filter((p) => p.is_active).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — ${parseFloat(p.price_usd).toFixed(2)}/{cycleLabel(p.billing_cycle as BillingCycle, t).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.table.status")}</label>
              <select
                value={assignStatus}
                onChange={(e) => setAssignStatus(e.target.value as SubscriptionStatus)}
                className="mt-1 w-full rounded-xl border border-black/10 ring-1 ring-black/5 bg-white px-3 py-2 text-sm text-gray-900"
              >
                <option value="trial">{t("superAdmin.hoaAdmins.status.trial")}</option>
                <option value="active">{t("superAdmin.hoaAdmins.status.active")}</option>
                <option value="suspended">{t("superAdmin.hoaAdmins.status.suspended")}</option>
                <option value="cancelled">{t("superAdmin.hoaAdmins.status.cancelled")}</option>
                <option value="expired">{t("superAdmin.hoaAdmins.status.expired")}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.table.notes")}</label>
              <Textarea
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder={t("superAdmin.hoaAdmins.table.notes")}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={() =>
                assignTarget &&
                updateSubMut.mutate({
                  id: assignTarget.id,
                  payload: {
                    plan: assignPlanId || null,
                    status: assignStatus,
                    notes: assignNotes,
                  },
                })
              }
              disabled={updateSubMut.isPending}
              className="rounded-xl text-white"
              style={{ backgroundColor: PRIMARY }}
            >
              {updateSubMut.isPending ? t("settings.saving") : t("settings.save")}
            </Button>
            <Button variant="outline" onClick={() => setAssignTarget(null)} className="rounded-xl">
              {t("superAdmin.hoaAdmins.actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/* --- Small bits --- */

function StatusPill({ admin }: { admin: HoaAdminUser }) {
  const { t } = useLanguage();
  if (admin.approval_status === "pending_approval") {
    return <Badge className="bg-amber-100 text-amber-900 ring-1 ring-amber-300">{t("superAdmin.hoaAdmins.status.pending")}</Badge>;
  }
  if (admin.approval_status === "rejected") {
    return <Badge className="bg-gray-100 text-gray-700 ring-1 ring-gray-300 line-through">{t("superAdmin.hoaAdmins.status.rejected")}</Badge>;
  }
  if (admin.subscription_status === "active") {
    return <Badge className="bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300">{t("superAdmin.hoaAdmins.status.active")}</Badge>;
  }
  if (admin.subscription_status === "suspended") {
    return <Badge className="bg-rose-100 text-rose-900 ring-1 ring-rose-300">{t("superAdmin.hoaAdmins.status.suspended")}</Badge>;
  }
  return <Badge className="bg-gray-100 text-gray-800 ring-1 ring-gray-300">{t("superAdmin.hoaAdmins.status.expired")}</Badge>;
}

/* ─────────────────────────────────────────────────────────────
   Subscription Payments Tab — super admin verifies HOA invoices
───────────────────────────────────────────────────────────── */

function getInvColors(t: (key: string) => string): Record<string, { bg: string; fg: string; label: string }> {
  return {
    PENDING_PAYMENT: {
      bg: "bg-amber-50",
      fg: "text-amber-700",
      label: t("superAdmin.payments.status.unpaid"),
    },
    PENDING_VERIFICATION: {
      bg: "bg-blue-50",
      fg: "text-blue-700",
      label: t("superAdmin.payments.status.pendingVerification"),
    },
    COMPLETED: {
      bg: "bg-green-50",
      fg: "text-green-700",
      label: t("superAdmin.hoaAdmins.table.completed"),
    },
    REJECTED: {
      bg: "bg-rose-50",
      fg: "text-rose-700",
      label: t("superAdmin.hoaAdmins.status.rejected"),
    },
    CANCELLED: {
      bg: "bg-gray-100",
      fg: "text-gray-600",
      label: t("superAdmin.hoaAdmins.status.cancelled"),
    },
  };
}

function SubscriptionPaymentsTab() {
  const { t, language } = useLanguage();
  const qc = useQueryClient();
  const dateLocale = language === "es" ? "es-ES" : "en-US";
  const [rejectTarget, setRejectTarget] = React.useState<SubscriptionInvoice | null>(null);
  const [rejectReason, setRejectReason] = React.useState("");

  const invoicesQuery = useQuery({
    queryKey: ["super-admin", "subscription-invoices"],
    queryFn: listSubscriptionInvoicesAdmin,
  });

  const verifyMut = useMutation({
    mutationFn: (id: string) => verifySubscriptionInvoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["super-admin", "subscription-invoices"] });
      toast.success(t("superAdmin.paymentDetail.toasts.verifySuccess"));
    },
    onError: () => toast.error(t("superAdmin.paymentDetail.toasts.verifyFail")),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectSubscriptionInvoice(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["super-admin", "subscription-invoices"] });
      setRejectTarget(null);
      setRejectReason("");
      toast.success(t("superAdmin.paymentDetail.toasts.proofRejected"));
    },
    onError: () => toast.error(t("superAdmin.payments.actions.rejecting")),
  });

  const invoices = invoicesQuery.data ?? [];
  const pending = invoices.filter((i) => i.status === "PENDING_VERIFICATION");
  const others = invoices.filter((i) => i.status !== "PENDING_VERIFICATION");

  if (invoicesQuery.isLoading) {
    return <div className="py-8 text-center text-sm text-gray-500">{t("superAdmin.hoaAdmins.states.loading")}</div>;
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="flex flex-wrap gap-3">
        <div className="rounded-2xl bg-blue-50 ring-1 ring-blue-200 px-4 py-3 text-center min-w-[110px]">
          <div className="text-2xl font-bold text-blue-700">{pending.length}</div>
          <div className="text-xs text-blue-600">{t("superAdmin.hoaAdmins.table.awaiting")}</div>
        </div>
        <div className="rounded-2xl bg-green-50 ring-1 ring-green-200 px-4 py-3 text-center min-w-[110px]">
          <div className="text-2xl font-bold text-green-700">
            {invoices.filter((i) => i.status === "COMPLETED").length}
          </div>
          <div className="text-xs text-green-600">{t("superAdmin.hoaAdmins.table.completed")}</div>
        </div>
        <div className="rounded-2xl bg-rose-50 ring-1 ring-rose-200 px-4 py-3 text-center min-w-[110px]">
          <div className="text-2xl font-bold text-rose-700">
            {invoices.filter((i) => i.status === "REJECTED").length}
          </div>
          <div className="text-xs text-rose-600">{t("superAdmin.hoaAdmins.status.rejected")}</div>
        </div>
      </div>

      {/* Pending verification list */}
      {pending.length === 0 && (
        <div className="rounded-2xl bg-gray-50 ring-1 ring-black/5 px-5 py-8 text-center">
          <p className="text-sm text-gray-500">{t("superAdmin.hoaAdmins.states.noPendingSubs")}</p>
        </div>
      )}

      {pending.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">{t("superAdmin.payments.status.pendingVerification")} ({pending.length})</h3>
          {pending.map((inv) => (
            <div key={inv.id} className="rounded-2xl bg-white ring-1 ring-black/8 shadow-sm p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getInvColors(t)[inv.status]?.bg} ${getInvColors(t)[inv.status]?.fg}`}>
                      {getInvColors(t)[inv.status]?.label}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{inv.hoa_admin_email}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-700">
                    <span className="font-medium">{inv.plan_detail?.name ?? t("superAdmin.hoaAdmins.states.noPlan")}</span>
                    {" — "}${inv.amount_usd}
                    {" · "}
                    {inv.payment_method ?? t("common.unknown")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("superAdmin.payments.table.date")}: {new Date(inv.created_at).toLocaleDateString(dateLocale)}
                  </p>
                </div>
                {inv.payment_proof_url && (
                  <a
                    href={inv.payment_proof_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded-xl bg-gray-50 ring-1 ring-black/10 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                  >
                    {t("superAdmin.hoaAdmins.actions.viewProof")}
                  </a>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => verifyMut.mutate(inv.id)}
                  disabled={verifyMut.isPending}
                  className="flex-1 rounded-xl py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition"
                >
                  {verifyMut.isPending ? t("superAdmin.payments.actions.approving") : t("superAdmin.payments.actions.confirmApproval")}
                </button>
                <button
                  onClick={() => { setRejectTarget(inv); setRejectReason(""); }}
                  className="rounded-xl px-4 py-2 text-sm font-medium bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100 transition"
                >
                  {t("superAdmin.hoaAdmins.actions.reject")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      {others.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t("superAdmin.hoaAdmins.table.history")}</h3>
          {others.map((inv) => (
            <div key={inv.id} className="flex items-start justify-between gap-3 rounded-xl bg-gray-50 ring-1 ring-black/5 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{inv.hoa_admin_email}</p>
                <p className="text-xs text-gray-500">
                  {inv.plan_detail?.name ?? t("superAdmin.hoaAdmins.states.noPlan")} · ${inv.amount_usd} · {new Date(inv.created_at).toLocaleDateString(dateLocale)}
                </p>
                {inv.rejection_reason && (
                  <p className="text-xs text-rose-600 mt-0.5">{t("superAdmin.hoaAdmins.dialogs.rejectionReason")}: {inv.rejection_reason}</p>
                )}
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${getInvColors(t)[inv.status]?.bg} ${getInvColors(t)[inv.status]?.fg}`}>
                {getInvColors(t)[inv.status]?.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Rechazar dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("superAdmin.hoaAdmins.dialogs.rejectPayment")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            {t("superAdmin.hoaAdmins.dialogs.rejectingPaymentFrom")} <strong>{rejectTarget?.hoa_admin_email}</strong>.
            {" "}<strong>{t("superAdmin.hoaAdmins.table.plan")}:</strong> <strong>{rejectTarget?.plan_detail?.name}</strong>.
          </p>
          <div className="space-y-2 mt-2">
            <label className="text-sm font-medium text-gray-700">{t("superAdmin.hoaAdmins.dialogs.rejectionReason")}</label>
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("superAdmin.hoaAdmins.dialogs.rejectionReason")}
            />
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={() => rejectTarget && rejectMut.mutate({ id: rejectTarget.id, reason: rejectReason })}
              disabled={rejectMut.isPending}
              className="rounded-xl text-white bg-rose-600 hover:bg-rose-700"
            >
              {rejectMut.isPending ? t("superAdmin.payments.actions.rejecting") : t("superAdmin.hoaAdmins.actions.reject")}
            </Button>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(""); }} className="rounded-xl">
              {t("superAdmin.hoaAdmins.actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({
  title,
  value,
  icon,
  tone = "slate",
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  tone?: "slate" | "emerald" | "amber" | "rose";
}) {
  const tones: Record<string, string> = {
    slate: "bg-gray-50 ring-gray-200 text-gray-900",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-900",
    amber: "bg-amber-50 ring-amber-200 text-amber-900",
    rose: "bg-rose-50 ring-rose-200 text-rose-900",
  };
  return (
    <div className={`rounded-2xl px-4 py-3 ring-1 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="grid place-items-center rounded-lg bg-white/80 ring-1 ring-black/5 p-1.5">{icon}</span>
        <span className="font-medium">{title}</span>
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function StatusSelect({
  value,
  onChange,
}: {
  value: AdminStatusUI;
  onChange: (v: AdminStatusUI) => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as AdminStatusUI)}
        className="rounded-xl border border-black/10 ring-1 ring-black/5 bg-white px-3 py-2 text-sm text-gray-900"
      >
        <option value="ALL">{t("superAdmin.hoaAdmins.status.all")}</option>
        <option value="pending">{t("superAdmin.hoaAdmins.status.pending")}</option>
        <option value="active">{t("superAdmin.hoaAdmins.status.active")}</option>
        <option value="suspended">{t("superAdmin.hoaAdmins.status.suspended")}</option>
        <option value="rejected">{t("superAdmin.hoaAdmins.status.rejected")}</option>
        <option value="inactive">{t("superAdmin.hoaAdmins.status.expired")}</option>
      </select>
    </div>
  );
}
